import { useEffect, useRef, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, clientApi, setClientId } from '@/lib/api-client'
import { isValidClientId } from '@/lib/client-id'
import { buildWizardDraftPatch, mapDraftToForm, type WizardFormValues } from '@/lib/wizard-draft'

export type WizardSaveState = 'idle' | 'loading' | 'saving' | 'saved' | 'error' | 'locked'

const AUTOSAVE_DELAY_MS = 600

type UseWizardAutosaveOptions = {
  enabled?: boolean
  onLocked?: () => void
}

export function useWizardAutosave(
  form: UseFormReturn<WizardFormValues>,
  options: UseWizardAutosaveOptions = {},
) {
  const { enabled = true, onLocked } = options
  const queryClient = useQueryClient()
  const [saveState, setSaveState] = useState<WizardSaveState>('loading')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasUnsavedChangesRef = useRef(false)
  const skipUnmountFlushRef = useRef(false)
  const hydrateReadyRef = useRef(false)
  const onLockedRef = useRef(onLocked)
  onLockedRef.current = onLocked

  function handlePatchError(err: unknown, keepalive: boolean) {
    if (err instanceof ApiError && err.status === 409) {
      setSaveState('locked')
      onLockedRef.current?.()
      return
    }
    if (import.meta.env.DEV && keepalive) {
      console.warn('[useWizardAutosave] keepalive PATCH failed', err)
    }
    if (!keepalive) setSaveState('error')
  }

  function patchDraftValues(values: WizardFormValues, keepalive = false) {
    if (!enabled) return
    void clientApi
      .patchDraft(buildWizardDraftPatch(values), { keepalive })
      .then((response) => {
        if (isValidClientId(response.client_id)) {
          setClientId(response.client_id)
          void queryClient.invalidateQueries({ queryKey: ['client', 'status', response.client_id] })
        }
        void queryClient.invalidateQueries({ queryKey: ['wizard-draft'] })
        hasUnsavedChangesRef.current = false
        if (!keepalive) setSaveState('saved')
      })
      .catch((err) => handlePatchError(err, keepalive))
  }

  function flushOrSaveDirty(keepalive = false) {
    if (!enabled) return
    const pendingTimer = timerRef.current
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer)
      timerRef.current = null
    }
    if (pendingTimer !== null || hasUnsavedChangesRef.current) {
      patchDraftValues(form.getValues(), keepalive)
    }
  }

  useEffect(() => {
    if (!enabled) {
      setSaveState('idle')
      return
    }

    let cancelled = false
    hydrateReadyRef.current = false

    async function hydrate() {
      try {
        const draft = await clientApi.getDraft()
        if (cancelled) return

        const mapped = mapDraftToForm(draft.draft_data)
        if (Object.keys(mapped).length > 0 && !hasUnsavedChangesRef.current) {
          form.reset({ ...form.getValues(), ...mapped })
          hasUnsavedChangesRef.current = false
        }

        if (isValidClientId(draft.client_id)) {
          setClientId(draft.client_id)
        }

        setSaveState('idle')
      } catch {
        if (!cancelled) setSaveState('idle')
      } finally {
        if (!cancelled) hydrateReadyRef.current = true
      }
    }

    void hydrate()

    return () => {
      cancelled = true
    }
  }, [enabled, form])

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    const subscription = form.watch(() => {
      if (!hydrateReadyRef.current) return

      hasUnsavedChangesRef.current = true

      if (timerRef.current) clearTimeout(timerRef.current)

      timerRef.current = setTimeout(() => {
        timerRef.current = null
        setSaveState('saving')
        patchDraftValues(form.getValues())
      }, AUTOSAVE_DELAY_MS)
    })

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        flushOrSaveDirty(true)
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      subscription.unsubscribe()
      if (!skipUnmountFlushRef.current) {
        flushOrSaveDirty(true)
      }
    }
  }, [enabled, form])

  async function saveNow(): Promise<void> {
    if (!enabled) return
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setSaveState('saving')
    try {
      const response = await clientApi.patchDraft(buildWizardDraftPatch(form.getValues()))
      if (isValidClientId(response.client_id)) {
        setClientId(response.client_id)
      }
      setSaveState('saved')
      hasUnsavedChangesRef.current = false
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSaveState('locked')
        onLockedRef.current?.()
        return
      }
      setSaveState('error')
      throw new Error('save failed')
    }
  }

  function prepareForNavigation(): void {
    skipUnmountFlushRef.current = true
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    hasUnsavedChangesRef.current = false
  }

  return { saveState, saveNow, prepareForNavigation }
}
